import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import { CompanyRole } from "../../../../auth/AuthProvider";
import { useAuth } from "../../../../auth/useAuth";

interface DoubleSelectDialogProps<T> {
  data: T[];
  subData: any[];
  name: string;
  label: string;
  onChange: any;
  values: any;
  valueField?: keyof T;
  nameField?: keyof T;
  MenuProps?: any;
  [x: string]: any;
}

export function DoubleSelectDialog<
  T extends { id: string; role?: CompanyRole; name?: string }
>(props: DoubleSelectDialogProps<T>) {
  const {
    data,
    subData,
    name,
    label,
    onChange,
    values,
    valueField,
    nameField,
    setValue,
    ...other
  } = props;
  const [open, setOpen] = useState(false);
  const [openSubDialog, setOpenSubDialog] = useState(false);
  const [tempValue, setTempValue] = useState<any>(null);
  const [emailNotificationEnabled, setEmailNotificationEnabled] =
    useState(false);
  const [role, setRole] = useState(CompanyRole.ADMIN);
  const { companyRoleObj, isSuperAdmin } = useAuth();

  const getValueField = (elem: any) => (valueField ? elem[valueField] : elem);
  const getNameField = (elem: T) => (nameField ? elem[nameField] : elem);

  useEffect(() => {
    if (tempValue) {
      setEmailNotificationEnabled(
        !tempValue?.role ? true : tempValue.emailNotificationEnabled
      );
      setRole(!tempValue?.role ? CompanyRole.ADMIN : tempValue.role);
    }
  }, [tempValue]);

  useEffect(() => {
    if (tempValue && !openSubDialog) {
      const selectedValue = values.find(
        (value: T) => value.id === tempValue.id
      );

      if (selectedValue) {
        const newValues = values.filter((value: T) => value !== selectedValue);
        if (tempValue.role) {
          newValues.push(tempValue);
        }
        onChange(newValues);
      } else {
        if (tempValue.role) {
          values.push(tempValue);
          onChange(values);
        }
      }
      setTempValue(null);
    }
  }, [onChange, tempValue, openSubDialog]);

  return (
    <FormControl fullWidth {...other}>
      <InputLabel htmlFor='grouped-select'>{label}</InputLabel>
      <Select
        variant='filled'
        name={name}
        label={label}
        onChange={(e) =>
          setTempValue(
            values.find((value: T) => value.id === e.target.value.id) ??
              e.target.value
          )
        }
        value={values}
        renderValue={() =>
          values
            .map((value: T) => `${getNameField(value)} (${value.role})`)
            .join(", ")
        }
        onClick={(e) => {
          e.stopPropagation();
          setTempValue(null);
          setOpenSubDialog(false);
          setEmailNotificationEnabled(false);
          setOpen(true);
        }}
        open={open}
      >
        <Stack alignItems='flex-end'>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            Done
          </Button>
        </Stack>
        {data.map((primaryElem, index) => {
          const checked = values.some(
            (value: T) => value.id === (primaryElem.id as string)
          );
          return (
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                setOpenSubDialog(true);
              }}
              key={index}
              value={getValueField(primaryElem)}
              disabled={
                !(
                  companyRoleObj[primaryElem.id] === CompanyRole.ADMIN ||
                  isSuperAdmin
                )
              }
            >
              <Checkbox
                onClick={(e: any) => {
                  if (!e.target.checked) {
                    e.stopPropagation();
                    setTempValue(() => ({
                      ...getValueField(primaryElem),
                      role: null,
                    }));
                  }
                }}
                size='small'
                checked={checked}
              />
              {getNameField(primaryElem)}{" "}
              {checked
                ? `(${
                    values.find((value: any) => value.id === primaryElem.id)
                      .role
                  })`
                : ""}
            </MenuItem>
          );
        })}
        <Dialog
          maxWidth='xs'
          open={openSubDialog}
          onClick={(e) => e.stopPropagation()}
          id='create-app-dialog'
        >
          <DialogTitle onClick={(e) => e.stopPropagation()}>
            {tempValue?.name} settings
          </DialogTitle>
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <Stack>
              <FormControlLabel
                sx={{ padding: "0 12px", mb: 1 }}
                control={<Checkbox />}
                label={"Receive notifications for this company"}
                name={"emailNotificationEnabled"}
                checked={emailNotificationEnabled}
                onChange={(e: any) => {
                  e.stopPropagation();
                  setEmailNotificationEnabled((prev: boolean) => !prev);
                }}
              />
              <FormControl {...other}>
                <InputLabel htmlFor='grouped-select'>Role</InputLabel>
                <Select
                  variant='filled'
                  name='role'
                  label='Role'
                  onChange={(e) => setRole(e.target.value as CompanyRole)}
                  value={role}
                >
                  {subData.map((subItem) => (
                    <MenuItem value={subItem}>{subItem}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions onClick={(e) => e.stopPropagation()}>
            <Stack flexDirection='row' alignItems='center'>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setTempValue(null);
                  setOpenSubDialog(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setTempValue((prev: any) => ({
                    ...prev,
                    role,
                    emailNotificationEnabled,
                  }));
                  setOpenSubDialog(false);
                }}
              >
                Save
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>
      </Select>
    </FormControl>
  );
}
